
import React from "react";
import { Schemes, Utils } from "../../../../../utils";
import { i18nManager } from "../../../../../i18n";
import { Elements } from "../../../../shared/Elements";
import { Siren } from "../../../../shared/ConfirmDialog";
import * as ReactRouterDom from "react-router-dom";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { BaseService, ConsusService, IMember, IProfileWebsite, ITeam, IUploadedFile, MemberService, SanitizedResponse, TeamService } from "../../../../../services";
import { Button, ButtonManageRef, Checkbox, ComposedPassword, DOMHelper, Dropdown, Form, FormControl, NoseurObject, ObjectHelper, TabPane, TabPanel, TextAreaInput, TextInput } from "@rivtn/noseur";
import ImportExport from "../../../../page_design/ImportExport";
import MediaInput, { MediaInputManageRef } from "../../../../shared/MediaInput";

export interface BasicProps {
    team: ITeam;
    profile: IMember;
}

function Basic(props: BasicProps) {

    let { team } = props;
    const teamService = TeamService.getInstance();
    const consusService = ConsusService.getInstance();
    const [profile, _] = React.useState<IMember>(props.profile);
    const labels = i18nManager.Labels.teams.settings.general.basic;
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const mediaInputManageRef = React.useRef<MediaInputManageRef>(null);
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);

    return (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-end" }}>
            <MediaInput manageRef={mediaInputManageRef} style={{ alignSelf: "center", marginBottom: 0 }} width={190} height={190}
                default={team?.logo_url} noFillTemplate onGenerateWithAi={async () => {
                    return new File(["We are here"], "we.txt", { type: 'text/plain' });
                }} onSelectFiles={(files) => {
                    consusService.uploadFile("/omnehost/team/", `logo_${team?.external_id}`, files[0]).then(({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                        updateTeam({ ...team, logo_url: sanitized.consus_download_url });
                    }).catch(({ errorMessage }: SanitizedResponse<any>) => {
                        setErrorMessage(errorMessage);
                    });
                }} />
            <div style={{ flex: 1, marginLeft: 20 }}>
                <FormControl isFieldset fill label={labels.team_id} style={{ marginTop: 15 }} scheme={Schemes.OMNEHOST}
                    rightContent={<i className="fa fa-copy" style={{ cursor: "pointer" }} onClick={() => DOMHelper.copyToClipboard(team.public_id, () => {
                        Siren.alert(i18nManager.Labels.common.copied, { lifetime: 1000 });
                    })} />}>
                    <TextInput fill disabled defaultValue={team.public_id} />
                </FormControl>
                <FormControl isFieldset fill label={labels.name} style={{ marginTop: 15 }} scheme={Schemes.OMNEHOST}>
                    <TextInput defaultValue={team.name} fill onInputComplete={(value: string) => {
                        team.name = value;
                    }} />
                </FormControl>
                <ComposedPassword inputProps={{ defaultValue: team.extra_data?.passcode_display }} scheme={Schemes.OMNEHOST} formControlProps={{
                    fill: true,
                    isFieldset: true,
                    label: labels.passcode,
                    style: { marginTop: 15 },
                }} onInputComplete={(value: string) => {
                    team.passcode = value;
                }} />
            </div>
        </div>
        <FormControl isFieldset fill label={labels.homepage} style={{ marginTop: 15 }} scheme={Schemes.OMNEHOST}>
            <TextInput defaultValue={team.homepage} fill onInputComplete={(value: string) => {
                team.homepage = value;
            }} />
        </FormControl>
        <FormControl isFieldset fill label={labels.email_address} style={{ marginTop: 15 }} scheme={Schemes.OMNEHOST}>
            <TextInput defaultValue={team.extra_data?.email_address} fill onInputComplete={(value: string) => {
                (team as any).email_address = value;
            }} />
        </FormControl>
        <FormControl isFieldset fill label={labels.country} style={{ marginTop: 15 }} scheme={Schemes.OMNEHOST}>
            <Dropdown fill options={i18nManager.CountriesAndCurrencies} optionMap={{ label: "label", value: "value" }}
                selectedOptionIndex={i18nManager.CountriesAndCurrencies?.findIndex(c => c.value === team.country_code)}
                onSelectOption={(country) => {
                    team.country_code = country.value;
                }} />
        </FormControl>
        <Dropdown fill selectedOptionIndex={i18nManager.Labels.account.profile_website.general.basic.visibility.options?.findIndex((o) => o.value === team.visibility)}
            style={{ width: "100%" }} formControlProps={{
                fill: true,
                isFieldset: true,
                style: { marginTop: 8 },
                label: labels.visibility,
            }} scheme={Schemes.OMNEHOST} options={i18nManager.Labels.account.profile_website.general.basic.visibility.options}
            onSelectOption={(o) => {
                team.visibility = o.value;
            }} />
        <FormControl isFieldset fill label={labels.description} style={{ marginTop: 15 }} scheme={Schemes.OMNEHOST}>
            <TextAreaInput style={{ height: 200 }} defaultValue={team.description} fill onInputComplete={(value: string) => {
                team.description = value;
            }} />
        </FormControl>
        {errorMessage && (<div className="error">{errorMessage}</div>)}
        <Button scheme={Schemes.OMNEHOST} loadingProps={Elements.BUTTON_LOADING_PROPS} text={i18nManager.Labels.common.save_changes}
            style={{ marginTop: 30, padding: "10px 25px", alignSelf: "flex-end" }} onClick={(e) => {
                updateTeam({ ...team }, e.manageRef);
            }} />
    </div>);

    function updateTeam(payload: ITeam, manageRef?: ButtonManageRef) {
        setErrorMessage("");
        manageRef?.setLoadingState(true);
        teamService.updateTeam(payload.external_id, payload).then(({ sanitized }: SanitizedResponse<ITeam>) => {
            Siren.alert(labels.update_success);
        }).catch(({ errorMessage }: SanitizedResponse<any>) => {
            setErrorMessage(errorMessage);
        }).finally(() => {
            manageRef?.setLoadingState(false);
        });
    }

}

export default Basic;
